import { useState, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Tables } from '@/integrations/supabase/types'

export type OrganizationInvite = Tables<'organization_invites'>
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://oitlhxqyfcxwterungzs.supabase.co";

export function useInvites() {
  const [loading, setLoading] = useState(false)

  const sendInvite = useCallback(async (orgId: string, email: string) => {
    try {
      setLoading(true)
      
      // Get current user
      const { data: userData, error: userError } = await supabase.auth.getUser()
      if (userError || !userData.user) {
        throw new Error('Usuário não autenticado')
      }
      
      // Check if user can invite (is owner or admin)
      const { data: memberData, error: memberError } = await supabase
        .from('organization_members')
        .select('role')
        .eq('org_id', orgId)
        .eq('user_id', userData.user.id)
        .single()
      
      if (memberError || !memberData) {
        throw new Error('Usuário não é membro desta organização')
      }
      
      if (!['owner', 'admin'].includes(memberData.role)) {
        throw new Error('Apenas proprietários e administradores podem convidar membros')
      }
      
      // Cannot invite yourself
      if (userData.user.email === email) {
        throw new Error('Você não pode convidar a si mesmo')
      }
      
      // Check if invited email is already a member or has pending invite
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email)
        .maybeSingle()

      if (existingUser) {
        // Check if user is already a member
        const { data: existingMember } = await supabase
          .from('organization_members')
          .select('id')
          .eq('org_id', orgId)
          .eq('user_id', existingUser.id)
          .maybeSingle()

        if (existingMember) {
          throw new Error('Este usuário já é membro desta organização')
        }
      }

      // Check if there's already a pending invite
      const { data: existingInvite } = await supabase
        .from('organization_invites')
        .select('id')
        .eq('org_id', orgId)
        .eq('invited_email', email)
        .eq('status', 'pending')
        .maybeSingle()

      if (existingInvite) {
        throw new Error('Já existe um convite pendente para este email')
      }
      
      const { data, error } = await supabase
        .from('organization_invites')
        .insert({
          org_id: orgId,
          invited_email: email,
          invited_by: userData.user.id
        })
        .select()
        .single()

      if (error) {
        console.error('Erro ao enviar convite:', error)
        throw error
      }

      // Chamar Edge Function para enviar e-mail
      try {
        const { data: { session } } = await supabase.auth.getSession()
        
        if (session?.access_token) {
          const response = await fetch(`${SUPABASE_URL}/functions/v1/send-invite-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ invite_id: data.id }),
          })

          if (!response.ok) {
            const errorText = await response.text()
            console.error('❌ Erro ao enviar e-mail de convite:', errorText)
            console.error('📧 Convite criado, mas e-mail pode não ter sido enviado')
            console.error('💡 Verifique se EMAIL_SERVICE e EMAIL_API_KEY estão configuradas no Supabase')
            // Não lançar erro aqui, pois o convite já foi criado
          } else {
            const emailResult = await response.json()
            if (emailResult.success) {
              console.log('✅ E-mail de convite enviado com sucesso:', emailResult)
            } else {
              console.warn('⚠️ E-mail não enviado:', emailResult.message)
              console.warn('💡 Configure EMAIL_SERVICE e EMAIL_API_KEY no Supabase para enviar e-mails')
            }
          }
        }
      } catch (emailError) {
        console.warn('Erro ao chamar função de envio de e-mail (convite criado, mas e-mail pode não ter sido enviado):', emailError)
        // Não lançar erro aqui, pois o convite já foi criado
      }

      return data
    } catch (error) {
      console.error('Erro ao enviar convite:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const acceptInvite = useCallback(async (inviteId: string) => {
    try {
      setLoading(true)
      
      const { data, error } = await supabase
        .rpc('accept_organization_invite', { invite_id: inviteId })

      if (error) {
        console.error('Erro ao aceitar convite:', error)
        throw error
      }

      return data
    } catch (error) {
      console.error('Erro ao aceitar convite:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const declineInvite = useCallback(async (inviteId: string) => {
    try {
      setLoading(true)
      
      const { data, error } = await supabase
        .rpc('decline_organization_invite', { invite_id: inviteId })

      if (error) {
        console.error('Erro ao recusar convite:', error)
        throw error
      }

      return data
    } catch (error) {
      console.error('Erro ao recusar convite:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const getPendingInvites = useCallback(async (orgId: string) => {
    try {
      const { data, error } = await supabase
        .from('organization_invites')
        .select('*')
        .eq('org_id', orgId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Erro ao buscar convites pendentes:', error)
        throw error
      }

      return data || []
    } catch (error) {
      console.error('Erro ao buscar convites pendentes:', error)
      throw error
    }
  }, [])

  const cancelInvite = useCallback(async (inviteId: string) => {
    try {
      setLoading(true)
      
      const { error } = await supabase
        .from('organization_invites')
        .update({ status: 'expired' })
        .eq('id', inviteId)

      if (error) {
        console.error('Erro ao cancelar convite:', error)
        throw error
      }

      return true
    } catch (error) {
      console.error('Erro ao cancelar convite:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    sendInvite,
    acceptInvite,
    declineInvite,
    getPendingInvites,
    cancelInvite
  }
}

